// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import {
  type Part,
  type AssemblyPartItem,
  PART_TYPE_ASSEMBLY_ID,
  type AssemblyWriteInItem,
  type CustomAssemblyJson,
  type StrongOmit,
  type TaskType,
} from '@evolvemep/api-ts';
import { type Node, useReactFlow } from '@xyflow/react';

import { isNil } from 'helpers/isNotNil';
import { maxPartNodeWidth, partNodeHeight, taskNodeHeight } from 'modules/Shop/CatalogSetup/PartEditor/nodes/constants';
import type { NodeType, assemblyNodeTypes, CoreNodeType } from 'modules/Shop/CatalogSetup/PartEditor/nodes/types';
import type { CustomAssembly } from 'modules/Shop/CatalogSetup/PartEditor/types';
import type { WriteInItemFields } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/EditItemPopover';
import type { PartToAdd } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/CatalogPartAdder';

export const getNodeInfoFromAssemblyTask = (
  task: Part['assemblyTasks'][number],
): StrongOmit<NodeType, 'id' | 'position'> & { type: keyof typeof assemblyNodeTypes } => ({
  type: 'task',
  data: {
    taskType: {
      taskTypeName: task.taskTypeName,
      taskTypeId: task.taskTypeId,
      taskTypeDescription: '', // task.taskTypeName,
      imageFileId: undefined, // task.image?.fileId,
    },
  },
});

export const getNodeInfoFromAssemblyPartItem = (
  item: AssemblyPartItem,
): StrongOmit<NodeType, 'id' | 'position'> & { type: keyof typeof assemblyNodeTypes } => ({
  type: item.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'assembly' : 'part',
  data: {
    part: item,
    quantity: item.unitQuantity,
  },
});

export const getNodeInfoFromAssemblyWriteInItem = (
  item: AssemblyWriteInItem,
): StrongOmit<NodeType, 'id' | 'position'> & { type: keyof typeof assemblyNodeTypes } => ({
  type: 'write-in',
  data: {
    part: item,
    quantity: item.unitQuantity,
  },
});

export const getNodeInfoFromCorePart = (
  part: CoreNodeType['data']['part'],
): StrongOmit<NodeType, 'id' | 'position'> & { type: keyof typeof assemblyNodeTypes } => ({
  type: 'core',
  deletable: false,
  data: {
    part,
  },
});

const bottomLeftMostNode = (nodes: Node[]) =>
  nodes.reduce((out: Node | null, curr) => {
    if (isNil(out)) return curr;
    if (curr.position.x < out.position.x) {
      return curr;
    }
    if (curr.position.x === out.position.x && curr.position.y > out.position.y) {
      return curr;
    }
    return out;
  }, null);

const bottomRightMostNode = (nodes: Node[]) =>
  nodes.reduce((out: Node | null, curr) => {
    if (isNil(out)) return curr;
    const currRightX = curr.position.x + (curr.measured?.width ?? 0);
    const outRightX = out.position.x + (out.measured?.width ?? 0);
    if (currRightX > outRightX) {
      return curr;
    }
    if (currRightX === outRightX && curr.position.y > out.position.y) {
      return curr;
    }
    return out;
  }, null);

export const useCalculatePartNodeStartingPoint = () => {
  const { getNodes, getNodesBounds } = useReactFlow();
  const calculatePartNodeStartingPoint = useCallback(() => {
    const existingNodes = getNodes();
    const leftMostPart = bottomLeftMostNode(
      existingNodes.filter((n) => n.type === 'part' || n.type === 'assembly' || n.type === 'write-in'),
    );
    const leftMostTask = bottomLeftMostNode(existingNodes.filter((n) => n.type === 'task'));
    const bounds = getNodesBounds(existingNodes);
    let { x, y } =
      isNil(leftMostPart) || (leftMostTask?.position.x ?? bounds.x) < leftMostPart.position.x
        ? bounds
        : leftMostPart.position;

    if (existingNodes.length === 1) {
      x -= maxPartNodeWidth * 0.5;
      y -= partNodeHeight;
    }

    if (x !== leftMostPart?.position.x) {
      x -= maxPartNodeWidth - 12;
      y -= partNodeHeight;
    } else {
      y += partNodeHeight + 12;
    }
    return { x, y };
  }, [getNodesBounds, getNodes]);
  return { calculatePartNodeStartingPoint };
};

export const useCreateNodesFromParts = () => {
  const { calculatePartNodeStartingPoint } = useCalculatePartNodeStartingPoint();
  const createNodesFromParts = useCallback(
    (parts: PartToAdd[]): StrongOmit<NodeType, 'id'>[] => {
      // oxlint-disable-next-line prefer-const
      let { x, y } = calculatePartNodeStartingPoint();
      return parts.map(({ part, quantity }) => {
        const node: StrongOmit<NodeType, 'id'> = {
          type: part.partTypeId === PART_TYPE_ASSEMBLY_ID ? 'assembly' : 'part',
          data: {
            part,
            quantity,
          },
          position: { x, y },
        };
        y += partNodeHeight + 12;
        return node;
      });
    },
    [calculatePartNodeStartingPoint],
  );
  const createNodeFromWriteInItem = useCallback(
    (item: WriteInItemFields): StrongOmit<NodeType, 'id'> => ({
      type: 'write-in',
      data: {
        part: item,
        quantity: item.quantity,
      },
      position: calculatePartNodeStartingPoint(),
    }),
    [calculatePartNodeStartingPoint],
  );
  return { createNodesFromParts, createNodeFromWriteInItem };
};

export const useCalculateTaskNodeStartingPoint = () => {
  const { getNodes, getNodesBounds } = useReactFlow();
  const calculateTaskNodeStartingPoint = useCallback(() => {
    const existingNodes = getNodes();
    const rightMostPart = bottomRightMostNode(existingNodes.filter((n) => n.type !== 'task'));
    const rightMostTask = bottomRightMostNode(existingNodes.filter((n) => n.type === 'task'));

    const bounds = getNodesBounds(existingNodes);
    let { x, y } =
      isNil(rightMostTask) ||
      (rightMostPart?.position.x ?? bounds.x) > rightMostTask.position.x + (rightMostTask.measured?.width ?? 0)
        ? {
            ...bounds,
            x: bounds.x + bounds.width,
          }
        : rightMostTask.position;

    if (existingNodes.length === 1) {
      y -= taskNodeHeight * 3;
    }
    if (x !== rightMostTask?.position.x) {
      x += 30;
    } else {
      y += taskNodeHeight + 12;
    }
    return { x, y };
  }, [getNodes, getNodesBounds]);
  return { calculateTaskNodeStartingPoint };
};

export const useCreateNodesFromTasks = () => {
  const { calculateTaskNodeStartingPoint } = useCalculateTaskNodeStartingPoint();
  const createNodesFromTasks = useCallback(
    (tasks: TaskType[]): StrongOmit<NodeType, 'id'>[] => {
      // oxlint-disable-next-line prefer-const
      let { x, y } = calculateTaskNodeStartingPoint();
      return tasks.map(({ name, ...taskType }) => {
        const node = {
          type: 'task',
          data: {
            taskType: {
              taskTypeName: name,
              ...taskType,
            },
          },
          position: { x, y },
        };
        y += taskNodeHeight + 12;
        return node;
      });
    },
    [calculateTaskNodeStartingPoint],
  );
  return { createNodesFromTasks };
};

export const parseCustomAssemblyJson = (json: Part['assemblyEditorLayout']): CustomAssembly | null => {
  if (typeof json !== 'string' || !json.trim()) return null;
  try {
    const assembly = JSON.parse(atob(json)) as CustomAssembly;
    // Always try to be backwards compatible with version updates here.
    // All versions before 2 were pre-release, so no need for backwards compatibility for those.
    if (assembly.version !== 2) return null;
    return assembly;
  } catch (e) {
    // oxlint-disable-next-line no-console
    console.error('Could not parse Assembly data:', json, 'Reason:', e);
    return null;
  }
};

export const stringifyCustomAssemblyJson = (assembly: CustomAssembly): CustomAssemblyJson =>
  btoa(JSON.stringify(assembly)) as CustomAssemblyJson;
